import { type ComponentType, useCallback, useEffect, useMemo } from "react";
import { Check } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/_atoms/button";
import { HeaderLayout } from "@/components/_templates/header-layout";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { useDocumentTitle } from "@/hooks/use-document-title";

import {
  type CreateDataRequestStep,
  entityKey,
  STEPS,
  useCreateDataRequestStore,
} from "../components/create-data-request-dialog/create-data-request-state";
import { ConfirmationStep } from "../components/create-data-request-dialog/steps/confirmation-step";
import { EntitySelectionStep } from "../components/create-data-request-dialog/steps/entity-selection-step";
import { FieldSelectionStep } from "../components/create-data-request-dialog/steps/field-selection-step";
import { useCreateDataRequestWithFeedback } from "../hooks/api/use-create-data-request-with-feedback";
import { toRequestDueDate } from "../lib/due-date";
import { toTrimmedQuestionLabels } from "../lib/free-text-questions";

const stepComponentMap: Record<CreateDataRequestStep, ComponentType> = {
  entities: EntitySelectionStep,
  fields: FieldSelectionStep,
  confirmation: ConfirmationStep,
};

export function CreateDataRequestPage() {
  const { t } = useDomainTranslation("data-request");
  useDocumentTitle(t("create.title"));
  const { currentWorkspace } = useCurrentWorkspaceContext();
  const navigate = useNavigate();

  const currentStep = useCreateDataRequestStore((s) => s.currentStep);
  const reset = useCreateDataRequestStore((s) => s.reset);
  const nextStep = useCreateDataRequestStore((s) => s.nextStep);
  const prevStep = useCreateDataRequestStore((s) => s.prevStep);
  const title = useCreateDataRequestStore((s) => s.title);
  const dueDate = useCreateDataRequestStore((s) => s.dueDate);
  const selectedEntities = useCreateDataRequestStore((s) => s.selectedEntities);
  const fieldSelection = useCreateDataRequestStore((s) => s.fieldSelection);
  const enableDocuments = useCreateDataRequestStore((s) => s.enableDocuments);
  const freeTextQuestions = useCreateDataRequestStore((s) => s.freeTextQuestions);
  const confirmationAssigneeMap = useCreateDataRequestStore((s) => s.confirmationAssigneeMap);

  const freeTextQuestionLabels = useMemo(() => toTrimmedQuestionLabels(freeTextQuestions), [freeTextQuestions]);

  const { createDataRequest, loading: isCreating } = useCreateDataRequestWithFeedback();

  useEffect(() => {
    reset();
  }, [reset]);

  const currentStepIndex = STEPS.indexOf(currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === STEPS.length - 1;

  const canProceed = useCallback(() => {
    if (currentStep === "entities") {
      return title.trim().length > 0 && selectedEntities.length > 0 && dueDate !== null;
    }
    if (currentStep === "fields") {
      if (fieldSelection !== null && fieldSelection.length === 0 && !enableDocuments && freeTextQuestionLabels.length === 0)
        return false;
      return true;
    }
    return true;
  }, [currentStep, title, selectedEntities, fieldSelection, enableDocuments, freeTextQuestionLabels, dueDate]);

  const handleNext = useCallback(() => {
    if (isLastStep) {
      createDataRequest({
        title: title.trim(),
        dueDate: toRequestDueDate(dueDate!),
        workspaceId: currentWorkspace.id,
        entities: selectedEntities.map((e) => {
          const confirmationAssignment = confirmationAssigneeMap[entityKey(e)];
          return {
            entityId: e.entityId,
            entityType: e.entityType,
            assigneeId: confirmationAssignment?.assigneeId ?? e.assigneeId ?? undefined,
          };
        }),
        fieldSelection: fieldSelection ?? undefined,
        enableDocuments,
        freeTextQuestions: freeTextQuestionLabels.length > 0 ? freeTextQuestionLabels : undefined,
      });
    } else {
      nextStep();
    }
  }, [isLastStep, nextStep, createDataRequest, title, dueDate, currentWorkspace.id, selectedEntities, fieldSelection, enableDocuments, freeTextQuestionLabels, confirmationAssigneeMap]);

  const handleBack = useCallback(() => {
    if (isFirstStep) {
      navigate("..");
    } else {
      prevStep();
    }
  }, [isFirstStep, prevStep, navigate]);

  const stepLabelMap = useMemo<Record<CreateDataRequestStep, string>>(
    () => ({
      entities: t("create.stepEntities"),
      fields: t("create.stepFields"),
      confirmation: t("create.stepConfirmation"),
    }),
    [t],
  );

  const StepComponent = stepComponentMap[currentStep];

  return (
    <div className="flex h-full max-h-full flex-col overflow-hidden">
      <HeaderLayout>
        <span className="text-sm font-medium">{t("create.title")}</span>
      </HeaderLayout>

      <div className="flex items-center justify-between border-b px-6 py-2">
        <div className="flex gap-1">
          {STEPS.map((step, index) => {
            const isCompleted = index < currentStepIndex;
            const isCurrent = index === currentStepIndex;

            return (
              <div
                key={step}
                className={`flex items-center gap-1.5 text-xs ${
                  isCurrent
                    ? "font-medium text-foreground"
                    : isCompleted
                      ? "text-foreground"
                      : "text-muted-foreground"
                }`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${
                    isCompleted
                      ? "bg-foreground text-background"
                      : isCurrent
                        ? "bg-foreground text-background"
                        : "bg-muted text-muted-foreground"
                  }`}
                >
                  {isCompleted ? <Check className="h-3 w-3" /> : index + 1}
                </span>
                <span>{stepLabelMap[step]}</span>
                {index < STEPS.length - 1 && (
                  <span className="mx-1 text-muted-foreground">·</span>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleBack}>
            {t("create.back")}
          </Button>
          <Button
            size="sm"
            onClick={handleNext}
            disabled={!canProceed() || isCreating}
          >
            {isLastStep ? t("create.submit") : t("create.next")}
          </Button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="flex min-h-0 w-full flex-1 flex-col">
          <StepComponent />
        </div>
      </div>
    </div>
  );
}
